import { Alert, Button, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { IssueLine, IssueRun, RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { date, money, units as formatUnits } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';

export default function IssueRunSummary({ run }: { run: IssueRun }) {
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const total = run.lines.reduce((sum, line) => sum + line.amountPaid, 0);
  const certificateFor = (line: IssueLine) =>
    state.documents.find(
      (doc) =>
        doc.refId === run.id && doc.kind === 'UNIT_CERTIFICATE' && doc.holderId === line.holderId,
    );
  return (
    <Stack spacing={2}>
      {run.status === 'PENDING_APPROVAL' && (
        <Alert
          severity="warning"
          action={
            <Button component={RouterLink} to="/manager/approvals" size="small">
              Approvals
            </Button>
          }
        >
          Waiting for a second person to approve. In the demo, switch <strong>Signed in as</strong>{' '}
          to Priya Nair (approver) and approve it.
        </Alert>
      )}
      {run.status === 'ISSUED' && (
        <Alert
          severity="success"
          action={
            !run.certificatesReleasedAt && (
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  dispatch({ type: 'ISSUE_RUN_RELEASE_CERTIFICATES', id: run.id });
                  toast('Certificates released to investors');
                }}
              >
                Release certificates
              </Button>
            )
          }
        >
          Units issued and approved by {run.approvedBy}.{' '}
          {run.certificatesReleasedAt
            ? `Certificates released ${date(run.certificatesReleasedAt)}.`
            : 'Certificates are generated and held until you release them.'}
        </Alert>
      )}
      <SectionCard title={run.reference} action={<StatusChip status={run.status} />}>
        <KeyValueGrid
          columns={3}
          items={[
            { label: 'Settlement date', value: date(run.settlementDate) },
            { label: 'Issue price', value: `$${run.issuePrice.toFixed(4)}` },
            { label: 'Paid per unit', value: `$${run.paidPerUnit.toFixed(4)}` },
            { label: 'Applicants', value: run.lines.length },
            { label: 'Total', value: money(total) },
            {
              label: 'Units',
              value: formatUnits(run.lines.reduce((sum, line) => sum + line.units, 0)),
            },
            { label: 'Prepared by', value: run.createdBy },
            {
              label: 'Certificates',
              value: run.certificateRelease === 'AUTO' ? 'Released on issue' : 'Manual release',
            },
            { label: 'Offer', value: run.closeOffer ? 'Closed at issue' : 'Left open' },
          ]}
        />
      </SectionCard>
      <SectionCard noPadding>
        <DataTable
          dense
          rows={run.lines}
          rowKey={(row) => row.applicationId}
          columns={[
            {
              key: 'name',
              label: 'Investor',
              render: (row) => (
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {row.applicantName}
                </Typography>
              ),
            },
            {
              key: 'paid',
              label: 'Investment amount',
              align: 'right',
              render: (row) => money(row.amountPaid, true),
            },
            {
              key: 'units',
              label: 'Units',
              align: 'right',
              render: (row) => formatUnits(row.units),
            },
            {
              key: 'cert',
              label: 'Certificate',
              render: (row) => row.certificateNo ?? 'Numbered on approval',
            },
            {
              key: 'view',
              label: '',
              align: 'right',
              render: (row) => {
                const doc = certificateFor(row);
                return doc ? (
                  <Button size="small" onClick={() => setViewing(doc)}>
                    View
                  </Button>
                ) : null;
              },
            },
          ]}
        />
      </SectionCard>
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </Stack>
  );
}
